import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { Printer, ArrowRight } from "lucide-react";
import "../../Styles/PatientPaymentForm.css";
import { useTheme } from "../../context/ThemeContext";


// Updated AutoCalculateFields to include MEDICINE_CHARGES
const AutoCalculateFields = ({ previousBalance }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const total =
      Number(values.CONSULTING_CHARGES || 0) +
      Number(values.PANCHKARMA_CHARGES || 0) +
      Number(values.MEDICINE_CHARGES || 0); // ✅ Added MEDICINE_CHARGES

    const newBalance =
      Number(previousBalance || 0) + total - Number(values.CURRENT_PAYMENT || 0);

    setFieldValue("TOTAL", total, false);
    setFieldValue("NEW_BALANCE", newBalance, false);
    setFieldValue("PREVIOUS_BALANCE", previousBalance, false);
  }, [
    previousBalance,
    values.CONSULTING_CHARGES,
    values.PANCHKARMA_CHARGES,
    values.MEDICINE_CHARGES, // ✅ Added to dependency array
    values.CURRENT_PAYMENT,
    setFieldValue,
  ]);

  return null;
};

// Custom Spinner component
const Spinner = () => <div className="spinner"></div>;

const PrintReceiptHandler = ({ show, formData, patientName }) => {
  useEffect(() => {
    if (show) {
      window.printReceipt = () => {
        const printWindow = window.open("", "_blank");
        const date = new Date().toLocaleDateString("en-IN");

        printWindow.document.write(`
          <html>
            <head>
              <title>Payment Receipt - Amrutdhara</title>
              <style>
                body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; }
                .receipt-container { border: 2px solid #eee; padding: 30px; max-width: 600px; margin: auto; }
                .header { text-align: center; border-bottom: 2px solid #4caf50; padding-bottom: 20px; margin-bottom: 20px; }
                .hospital-name { font-size: 28px; font-weight: bold; color: #2e7d32; margin: 0; }
                .receipt-title { font-size: 18px; text-transform: uppercase; letter-spacing: 2px; color: #666; margin-top: 5px; }
                .info-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
                .label { font-weight: 600; color: #555; }
                .value { font-weight: bold; }
                .divider { border-top: 1px dashed #ccc; margin: 20px 0; }
                .total-row { background: #f9f9f9; padding: 15px; border-radius: 8px; margin-top: 20px; }
                .authorized-sign { margin-top: 50px; text-align: right; }
                @media print { .no-print { display: none; } }
              </style>
            </head>
            <body>
              <div class="receipt-container">
                <div class="header">
                  <h1 class="hospital-name">Amrutdhara Wellness</h1>
                  <div class="receipt-title">Payment Receipt</div>
                </div>
                
                <div class="info-row">
                  <span class="label">Receipt No:</span>
                  <span class="value">REC-${Date.now().toString().slice(-6)}</span>
                </div>
                <div class="info-row">
                  <span class="label">Date:</span>
                  <span class="value">${date}</span>
                </div>
                <div class="info-row">
                  <span class="label">Patient Name:</span>
                  <span class="value">${patientName || "N/A"}</span>
                </div>
                
                <div class="divider"></div>
                
                <div class="info-row">
                  <span>Consulting Charges</span>
                  <span>₹${formData.CONSULTING_CHARGES}</span>
                </div>
                <div class="info-row">
                  <span>Panchkarma Charges</span>
                  <span>₹${formData.PANCHKARMA_CHARGES}</span>
                </div>
                <div class="info-row">
                  <span>Medicine Charges</span>
                  <span>₹${formData.MEDICINE_CHARGES}</span>
                </div>
                <div class="info-row">
                  <span>Previous Balance</span>
                  <span>₹${formData.PREVIOUS_BALANCE}</span>
                </div>
                
                <div class="total-row">
                  <div class="info-row" style="font-size: 20px;">
                    <span class="label">Amount Received:</span>
                    <span class="value" style="color: #2e7d32;">₹${formData.CURRENT_PAYMENT}</span>
                  </div>
                </div>

                <div class="info-row" style="margin-top: 15px;">
                  <span class="label">Remaining Balance:</span>
                  <span class="value">₹${formData.NEW_BALANCE}</span>
                </div>

                <div class="authorized-sign">
                  <div style="border-top: 1px solid #333; display: inline-block; padding-top: 5px; min-width: 150px;">
                    Authorized Signature
                  </div>
                </div>
                
                <center class="no-print" style="margin-top: 30px;">
                  <button onclick="window.print()" style="padding: 10px 20px; background: #4caf50; color: white; border: none; border-radius: 5px; cursor: pointer;">Print Now</button>
                </center>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
      };
    }
  }, [show, formData, patientName]);

  return null;
};

// Main component with custom CSS
const PatientPaymentForm = ({
  initialValues,
  onSubmit,
  fetchPreviousBalance,
  onContinue,
  patientName,
}) => {
  const { theme, isDarkMode } = useTheme();
  const [previousBalance, setPreviousBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrintButton, setShowPrintButton] = useState(false);

  useEffect(() => {
    const getBalance = async () => {
      try {
        const balance = await fetchPreviousBalance();
        setPreviousBalance(balance || 0);
      } catch (error) {
        console.error("Error fetching previous balance:", error);
        setPreviousBalance(0);
      } finally {
        setLoading(false);
      }
    };
    getBalance();
  }, [fetchPreviousBalance]);

  // Initial values for Formik - ✅ Added MEDICINE_CHARGES
  const formInitialValues = {
    CONSULTING_CHARGES: initialValues?.CONSULTING_CHARGES || 0,
    PANCHKARMA_CHARGES: initialValues?.PANCHKARMA_CHARGES || 0,
    MEDICINE_CHARGES: initialValues?.MEDICINE_CHARGES || 0, // ✅ Added
    CURRENT_PAYMENT: initialValues?.CURRENT_PAYMENT || 0,
    TOTAL: 0,
    NEW_BALANCE: 0,
    PREVIOUS_BALANCE: previousBalance || 0,
  };

  // Validation schema - ✅ Added MEDICINE_CHARGES validation
  const validationSchema = Yup.object().shape({
    CONSULTING_CHARGES: Yup.number()
      .min(0, "Cannot be negative")
      .required("Required"),
    PANCHKARMA_CHARGES: Yup.number()
      .min(0, "Cannot be negative")
      .required("Required"),
    MEDICINE_CHARGES: Yup.number() // ✅ Added
      .min(0, "Cannot be negative")
      .required("Required"),
    CURRENT_PAYMENT: Yup.number()
      .min(0, "Cannot be negative")
      .required("Required"),
  });

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="payment-form-container" style={{ backgroundColor: 'transparent' }}>
      <div className="payment-form-card shadow-xl" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="payment-form-header" style={{ backgroundColor: theme.primary }}>
          <h4 className="header-title" style={{ color: '#fff' }}>Patient Payment Details</h4>
        </div>

        <div className="payment-form-body">
          <Formik
            enableReinitialize
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await onSubmit(values);
                setShowPrintButton(true);
              } catch (error) {
                console.error("Submission error:", error);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {(formikProps) => (
              <Form className="payment-form">
                <AutoCalculateFields previousBalance={previousBalance} />

                <PrintReceiptHandler show={showPrintButton} formData={formikProps.values} patientName={patientName} />

                <div className="form-row">
                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="consultingCharges" className="form-label" style={{ color: theme.text }}>
                        Consulting Charges
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <Field
                          id="consultingCharges"
                          name="CONSULTING_CHARGES"
                          type="number"
                          placeholder="0"
                          className={`form-control ${formikProps.touched.CONSULTING_CHARGES &&
                            formikProps.errors.CONSULTING_CHARGES
                            ? "is-invalid"
                            : ""
                            }`}
                        />
                      </div>
                      <ErrorMessage
                        name="CONSULTING_CHARGES"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="panchkarmaCharges" className="form-label" style={{ color: theme.text }}>
                        Panchkarma Charges
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <Field
                          id="panchkarmaCharges"
                          name="PANCHKARMA_CHARGES"
                          type="number"
                          placeholder="0"
                          className={`form-control ${formikProps.touched.PANCHKARMA_CHARGES &&
                            formikProps.errors.PANCHKARMA_CHARGES
                            ? "is-invalid"
                            : ""
                            }`}
                        />
                      </div>
                      <ErrorMessage
                        name="PANCHKARMA_CHARGES"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  {/* ✅ NEW FIELD: Medicine Charges */}
                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="medicineCharges" className="form-label" style={{ color: theme.text }}>
                        Medicine Charges
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <Field
                          id="medicineCharges"
                          name="MEDICINE_CHARGES"
                          type="number"
                          placeholder="0"
                          className={`form-control ${formikProps.touched.MEDICINE_CHARGES &&
                            formikProps.errors.MEDICINE_CHARGES
                            ? "is-invalid"
                            : ""
                            }`}
                        />
                      </div>
                      <ErrorMessage
                        name="MEDICINE_CHARGES"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  <div className="form-col-full">
                    <div className="form-group">
                      <label htmlFor="currentPayment" className="form-label" style={{ color: theme.text }}>
                        Fees Received
                      </label>
                      <div className="input-group">
                        <span className="input-group-text ">₹</span>
                        <Field
                          id="currentPayment"
                          name="CURRENT_PAYMENT"
                          type="number"
                          placeholder="0"
                          className={`form-control large-text ${formikProps.touched.CURRENT_PAYMENT &&
                            formikProps.errors.CURRENT_PAYMENT
                            ? "is-invalid"
                            : ""
                            }`}
                        />
                      </div>
                      <ErrorMessage
                        name="CURRENT_PAYMENT"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>
                </div>

                <hr className="divider" />

                <h5 className="summary-title" style={{ color: theme.textSecondary }}>Summary</h5>
                <div className="summary-row">
                  <div className="summary-box" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderColor: theme.border }}>
                    <h6 className="summary-box-title" style={{ color: theme.textSecondary }}>Prev. Balance</h6>
                    <Field name="PREVIOUS_BALANCE">
                      {({ field }) => (
                        <h4 className="summary-box-value text-info">
                          {field.value.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          })}
                        </h4>
                      )}
                    </Field>
                  </div>

                  <div className="summary-box" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderColor: theme.border }}>
                    <h6 className="summary-box-title" style={{ color: theme.textSecondary }}>Total Current Charges</h6>
                    <Field name="TOTAL">
                      {({ field }) => (
                        <h4 className="summary-box-value text-primary">
                          {field.value.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          })}
                        </h4>
                      )}
                    </Field>
                  </div>

                  <div className="summary-box" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderColor: theme.border }}>
                    <h6 className="summary-box-title" style={{ color: theme.textSecondary }}>New Balance</h6>
                    <Field name="NEW_BALANCE">
                      {({ field }) => (
                        <h4 className="summary-box-value text-success">
                          {field.value.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          })}
                        </h4>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="button-container">
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={formikProps.isSubmitting}
                  >
                    {formikProps.isSubmitting ? (
                      <>
                        <Spinner />
                        <span>Saving...</span>
                      </>
                    ) : (
                      "Save Payment"
                    )}
                  </button>

                  {showPrintButton && (
                    <div className="action-buttons-group">
                      <button
                        type="button"
                        className="print-button"
                        onClick={() => window.printReceipt()}
                      >
                        <Printer size={18} />
                        Print Receipt
                      </button>

                      <button
                        type="button"
                        className="continue-button"
                        onClick={onContinue}
                      >
                        Continue
                        <ArrowRight size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default PatientPaymentForm;